import { Injectable, OnDestroy } from '@angular/core';
import { StatsigService } from '@statsig/angular-bindings';
import { AuthTrackingService } from '@core/services/authTracking.service';
import { User } from '@core/redux/stores/user.store';
import { interval, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { FeatureFlagService } from './feature-flag.service';
import { buildStatsigUser } from './statsig-user-builder';

const BACKGROUND_REFRESH_MS = 5 * 60 * 1000; //5 min background refresh

/* Verification toggle: comment one line and uncomment the other to switch. This will be removed in the future, this is just for some easy testing*/
const VERIFY_FEATURE_FLAGS = false;

@Injectable()
export class StatsigFeatureFlagService extends FeatureFlagService implements OnDestroy {
  override isInitialized = false;
  private disposed = false;
  private verifyRan = false;
  private lastUser: User | null = null;
  private userSubscription?: Subscription;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly statsigService: StatsigService,
    private readonly authTrackingService: AuthTrackingService
  ) {
    super();
    this.userSubscription = this.authTrackingService.user.subscribe((user) => {
      this.lastUser = user ?? null;
      this.updateUser(user);
    });
    this.statsigService.isLoading$.subscribe((loading) => {
      const wasLoading = !this.isInitialized;
      if (!loading) this.isInitialized = true;
      if (VERIFY_FEATURE_FLAGS && !environment.production && wasLoading && this.isInitialized && !this.verifyRan) {
        this.verifyFeatureFlagsInterface();
      }
    });
    interval(BACKGROUND_REFRESH_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.disposed) return;
        const statsigUser = buildStatsigUser(this.lastUser);
        this.statsigService.updateUserAsync(statsigUser).catch(() => {});
      });
  }

  ngOnDestroy(): void {
    this.disposed = true;
    this.userSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  override checkGate(gateName: string): boolean {
    if (this.disposed) return false;
    return this.statsigService.checkGate(gateName);
  }

  override getConfigValue<T>(configName: string, paramName: string, defaultValue: T): T {
    if (this.disposed) return defaultValue;
    const config = this.statsigService.getDynamicConfig(configName);
    return (config?.get?.(paramName, defaultValue) as T) ?? defaultValue;
  }

  override getExperimentValue<T>(experimentName: string, paramName: string, defaultValue: T): T {
    if (this.disposed) return defaultValue;
    const experiment = this.statsigService.getExperiment(experimentName);
    return (experiment?.get?.(paramName, defaultValue) as T) ?? defaultValue;
  }

  override getLayerValue<T>(layerName: string, paramName: string, defaultValue: T): T {
    if (this.disposed) return defaultValue;
    const layer = this.statsigService.getLayer(layerName);
    return (layer?.get?.(paramName, defaultValue) as T) ?? defaultValue;
  }

  override logEvent(eventName: string, value?: string | number, metadata?: Record<string, string>): void {
    if (this.disposed) return;
    this.statsigService.logEvent(eventName, value, metadata);
  }

  override updateUser(user?: User | null): void {
    if (this.disposed) return;
    this.lastUser = user ?? null;
    const statsigUser = buildStatsigUser(this.lastUser);
    this.statsigService.updateUserSync(statsigUser);
  }

  override refreshUser(): void {
    if (this.disposed) return;
    const statsigUser = buildStatsigUser(this.lastUser);
    this.statsigService.updateUserAsync(statsigUser).catch(() => {});
  }

  override flush(): void {
    if (this.disposed) return;
    //not awaited: the caller is a page that is already going away. The SDK has flipped to sendBeacon
    //by this point, so the send is handed to the browser rather than left for a promise to resolve
    this.statsigService.getClient().flush().catch(() => {});
  }

  /** Verification logic — runs only when VERIFY_FEATURE_FLAGS is true. */
  private verifyFeatureFlagsInterface(): void {
    if (this.verifyRan || environment.production) return;
    this.verifyRan = true;

    const log = (msg: string) => console.debug('[FeatureFlags][Verify]', msg);

    // Refresh user context before evaluations — mirrors MAUI's UpdateUserAsync() call
    // in VerifyFeatureFlagsInterfaceAsync so results are for the correct user.
    try {
      this.updateUser(this.lastUser);
      log('UpdateUser: success');
    } catch (e) { log(`UpdateUser error: ${e}`); }

    // --- Gate ---
    try {
      const isOn = this.checkGate('show-upsell-recipe-tiles');
      log(`CheckGate(show-upsell-recipe-tiles): ${isOn ? 'On' : 'Off'}`);
    } catch (e) { log(`CheckGate error: ${e}`); }

    // --- Dynamic Config ---
    try {
      const configName = 'mobile_home_config';
      const bannerText  = this.getConfigValue(configName, 'banner_text',  'default-banner');
      const maxTiles    = this.getConfigValue(configName, 'max_tiles',    3);
      const showBadge   = this.getConfigValue(configName, 'show_new_badge', false);
      log(`GetConfigValue(${configName}): banner_text='${bannerText}', max_tiles=${maxTiles}, show_new_badge=${showBadge}`);

      const missingParam  = this.getConfigValue(configName, 'missing_param', 'missing-param-default');
      const missingConfig = this.getConfigValue('mobile_home_config_missing', 'banner_text', 'missing-config-default');
      log(`Config fallbacks: missing_param='${missingParam}', missing_config='${missingConfig}'`);
    } catch (e) { log(`GetConfigValue error: ${e}`); }

    // --- Experiment ---
    try {
      const experimentName = 'mobile_home_experiment';
      const title    = this.getExperimentValue(experimentName, 'title',    'default-experiment-title');
      const discount = this.getExperimentValue(experimentName, 'discount', 0);
      log(`GetExperimentValue(${experimentName}): title='${title}', discount=${discount}`);
    } catch (e) { log(`GetExperimentValue error: ${e}`); }

    // --- Layer ---
    try {
      const layerName  = 'mobile_home_layer';
      const title   = this.getLayerValue(layerName, 'title',   'default-layer-title');
      const enabled = this.getLayerValue(layerName, 'enabled', false);
      log(`GetLayerValue(${layerName}): title='${title}', enabled=${enabled}`);
    } catch (e) { log(`GetLayerValue error: ${e}`); }

    // --- LogEvent ---
    try {
      this.logEvent('ff_verify_string_event', 'debug-string-value', { source: 'FeatureFlagService' });
      log('LogEvent(string): sent');
      this.logEvent('ff_verify_number_event', 123.45, { source: 'FeatureFlagService' });
      log('LogEvent(number): sent');
    } catch (e) { log(`LogEvent error: ${e}`); }

    log('Verification complete');
  }
}
